import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Container, Form, Modal, OverlayTrigger, ProgressBar, Tooltip } from 'react-bootstrap';
import { Check2, Clipboard, Pen, PeopleFill, PlusCircle, Trash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import FormGenerator from '../../form-generator/FormGenerator';
import { setAttribute } from '../../form-generator/helpers/utility';
import { makeApiRequests } from '../../helpers/api';
import {
  APP_ROLES,
  ENDPOINTS,
  ROLE_ADMIN,
  ROLE_INSTALLER,
  ROLE_SALES,
  ROLE_SALES_AND_INVENTORY_MANAGER,
  ROLE_SALES_MANAGER,
  ROLE_SUPER_ADMIN
} from '../../helpers/constants';
import { getObjectForPrefill } from '../../helpers/formHelpers';
import { copyToClipboard } from '../../helpers/global';
import { isMultiLocationRole, isSuperAdmin } from '../../helpers/roleHelpers';
import AlertModal from '../AlertModal';
import EntityTable from '../common/EntityTable';
import Loader from '../Loader';
import { getUserAddRows, getUserUpdateRows, userHeaders } from './constants';

const ADMIN_ROLES = [ROLE_SUPER_ADMIN, ROLE_ADMIN];
const ROLES_NEEDING_SALES_LOCATION = [ROLE_SALES, ROLE_SALES_MANAGER, ROLE_SALES_AND_INVENTORY_MANAGER];
const ROLES_FOR_SALES_REP_MANAGER_FILTER = [ROLE_SALES, ROLE_SALES_AND_INVENTORY_MANAGER];

/** Single-location dropdown vs multi “Assigned Sales Locations” in add/edit user modal. */
const applyUserFormSalesLocationFieldVisibility = (rows, showMultiSalesLocationField) =>
  rows.map(row => ({
    ...row,
    columns: row.columns.map(col => {
      if (col.field.id === 'salesLocation') {
        return { ...col, className: `my-1 ${showMultiSalesLocationField ? 'd-none' : ''}` };
      }
      if (col.field.id === 'salesLocations') {
        return { ...col, className: `my-1 ${showMultiSalesLocationField ? '' : 'd-none'}` };
      }
      return col;
    })
  }));

const getUserFormPrefill = user => {
  const o = getObjectForPrefill(user);
  if (o.enabled !== undefined) o.enabled = String(o.enabled);
  if (isMultiLocationRole(user?.roles)) {
    if (Array.isArray(user.salesLocations) && user.salesLocations.length) {
      o.salesLocations = user.salesLocations.map(s => s._id || s);
    } else if (user.salesLocation) {
      o.salesLocations = [user.salesLocation._id || user.salesLocation];
    }
  }
  return o;
};

const userTableSearchKeys = ['name', 'email', 'phone', 'roles', 'salesLocationName'];

const SALES_LOCATION_FILTER_UNSPECIFIED = '__UNSPECIFIED_LOCATION__';

const getUserSalesLocationIds = u => {
  const ids = [];
  if (Array.isArray(u.salesLocations) && u.salesLocations.length) {
    u.salesLocations.forEach(sl => {
      const id = (sl && (sl._id || sl))?.toString?.();
      if (id) ids.push(id);
    });
  }
  if (u.salesLocation) {
    const id = (u.salesLocation._id || u.salesLocation)?.toString?.();
    if (id && !ids.includes(id)) ids.push(id);
  }
  return ids;
};

const userMatchesSalesLocationFilter = (u, selectedId) => {
  if (!selectedId) return true;
  if (selectedId === SALES_LOCATION_FILTER_UNSPECIFIED) {
    return getUserSalesLocationIds(u).length === 0;
  }
  return getUserSalesLocationIds(u).includes(String(selectedId));
};

const COPY_FEEDBACK_MS = 1600;

const copyBtnWrapStyle = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '1.25rem',
  minHeight: '1.25rem',
  verticalAlign: 'middle'
};

const copyCheckDoneStyle = {
  display: 'block',
  color: '#28a745'
};

const userSalesRepRow = (salesRepsOptions, userMeta) => {
  const groupedByAddress = salesRepsOptions
    ?.filter(repOption => repOption?.salesLocation?._id === userMeta?.salesLocation?._id)
    .reduce((acc, { name, _id, salesLocation }) => {
      const { location } = salesLocation;

      if (!acc[location]) {
        acc[location] = [];
      }

      acc[location].push({ label: name, value: _id });

      return acc;
    }, {});

  return [
    {
      columns: Object.keys(groupedByAddress).map(location => ({
        default: 6,
        field: {
          id: 'salesReps',
          type: 'checkbox-group',
          title: location,
          options: groupedByAddress[location].map(user => user.label),
          optionValues: groupedByAddress[location].map(user => user.value)
        }
      }))
    }
  ];
};

const getForm = ({ rows, type = 'Add', salesLocationOptions = [], installerOptions = [] }) => {
  const form = {
    forms: [
      {
        name: `${type} user`,
        markCompulsoryFields: true,
        hideFormName: true,
        compact: true,
        submit: {
          name: type,
          show: true,
          onSubmit: type === 'Add' ? 'onNewUserFormSubmit' : 'onUpdateUserFormSubmit'
        },
        rows
      }
    ]
  };

  setAttribute(
    form,
    'salesLocation',
    'options',
    salesLocationOptions.map(s => s.location)
  );
  setAttribute(
    form,
    'salesLocation',
    'optionValues',
    salesLocationOptions.map(s => s._id)
  );
  setAttribute(
    form,
    'salesLocations',
    'options',
    salesLocationOptions.map(s => s.location)
  );
  setAttribute(
    form,
    'salesLocations',
    'optionValues',
    salesLocationOptions.map(s => s._id)
  );
  setAttribute(
    form,
    'installer',
    'options',
    installerOptions.map(s => s.name)
  );
  setAttribute(
    form,
    'installer',
    'optionValues',
    installerOptions.map(s => s._id)
  );
  return form;
};

const AddEditModal = ({ show, toBeEditedUser, onHide, showProgress, addForm, updateForm, isForSalesReps }) => (
  <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
    <Modal.Header closeButton={!showProgress}>
      <Modal.Title>
        <h6 className="mb-0">
          {isForSalesReps ? 'Select Sales Representatives' : toBeEditedUser ? 'Update User' : 'Add New User'}
        </h6>
      </Modal.Title>
    </Modal.Header>
    <Modal.Body className="overflow-auto px-1">
      <FormGenerator
        formJson={toBeEditedUser ? updateForm : addForm}
        formValues={toBeEditedUser ? { 'Update user': getUserFormPrefill(toBeEditedUser) } : { 'Add user': { enabled: 'true' } }}
      />
      {showProgress && (
        <ProgressBar now={100} animated striped label={toBeEditedUser ? `Updating user...` : `Saving user....`} />
      )}
    </Modal.Body>
  </Modal>
);
const renderUserRolesCell = row => {
  const r = row.roles;
  if (!Array.isArray(r) || !r.length) return '-';
  const extra = r.length - 2;
  const badges = (
    <span className="d-inline-flex flex-wrap align-items-center" style={{ gap: 6 }}>
      {r.slice(0, 2).map((role, i) => (
        <Badge key={`${role}-${i}`} className="p-1 px-2" style={{ fontSize: 12 }} variant="primary">
          {role}
        </Badge>
      ))}
      {extra > 0 && (
        <Badge className="p-1 px-2" style={{ fontSize: 12 }} variant="primary">
          +{extra} {extra === 1 ? 'other' : 'others'}
        </Badge>
      )}
    </span>
  );
  if (r.length <= 1) return badges;
  const tipId = `users-roles-${String(row._id || row.email || r.join('|')).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  return (
    <OverlayTrigger
      placement="auto"
      delay={{ show: 200, hide: 100 }}
      overlay={
        <Tooltip id={tipId} className="roles-tooltip">
          <div className="text-left small" style={{ maxWidth: 280 }}>
            {r.map((role, i) => (
              <div key={`${role}-${i}`}>{role}</div>
            ))}
          </div>
        </Tooltip>
      }
    >
      <span className="d-inline-block" style={{ cursor: 'pointer' }} tabIndex={0}>
        {badges}
      </span>
    </OverlayTrigger>
  );
};

const renderUserLocationsCell = row => {
  const labels = [];
  if (Array.isArray(row.salesLocations) && row.salesLocations.length) {
    row.salesLocations.forEach(sl => {
      const loc = typeof sl === 'object' && sl?.location ? String(sl.location).trim() : '';
      if (loc) labels.push(loc);
    });
  } else if (row.salesLocation?.location) {
    labels.push(String(row.salesLocation.location).trim());
  }
  if (!labels.length) return '-';
  return (
    <span className="d-inline-flex flex-wrap align-items-center" style={{ gap: 6 }}>
      {labels.map((label, i) => (
        <Badge key={`${label}-${i}`} pill variant="secondary" className="font-weight-normal" style={{ fontSize: 12 }}>
          {label}
        </Badge>
      ))}
    </span>
  );
};

const userTableHeaders = userHeaders
  .filter(h => h.key !== 'salesLocations')
  .map(h => {
    if (h.key === 'name') return { ...h, renderCell: user => <span>{user.name}{user.reportingOnly && <Badge variant="secondary" className="ml-2">Reporting only · inactive</Badge>}</span> };
    if (h.key === 'email') return { ...h, renderCell: user => user.reportingOnly ? 'No login' : <a href={'mailto:' + user.email}>{user.email}</a> };
    if (h.key === 'roles') return { ...h, showAsBadge: false, renderCell: renderUserRolesCell };
    if (h.key === 'salesLocation') {
      return { ...h, name: 'Locations', showAsBadge: false, renderCell: renderUserLocationsCell };
    }
    return h;
  });

const Users = ({ appChoices = [] }) => {
  const [salesRepsOptions, setSalesRepsOptions] = useState([]);
  const [salesLocationOptions] = useState(appChoices.find(ac => ac.key === 'salesLocation').values);
  const [installerOptions] = useState(appChoices.find(ac => ac.key === 'installer').values);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [toBeDeletedUser, setToBeDeletedUser] = useState(null);
  const [userActionMeta, setUserActionMeta] = useState(null);
  const [userAddingUpdating, setUserAddingUpdating] = useState(false);
  const [roleFilter, setRoleFilter] = useState(null);
  const [salesLocationFilterId, setSalesLocationFilterId] = useState('');
  const [copiedTarget, setCopiedTarget] = useState(null);
  const copyFeedbackTimerRef = useRef(null);

  const showCopyFeedback = targetId => {
    if (copyFeedbackTimerRef.current) {
      clearTimeout(copyFeedbackTimerRef.current);
    }
    setCopiedTarget(targetId);
    copyFeedbackTimerRef.current = setTimeout(() => {
      setCopiedTarget(null);
      copyFeedbackTimerRef.current = null;
    }, COPY_FEEDBACK_MS);
  };

  useEffect(
    () => () => {
      if (copyFeedbackTimerRef.current) {
        clearTimeout(copyFeedbackTimerRef.current);
      }
    },
    []
  );
  const roleCounts = useMemo(() => {
    const counts = Object.fromEntries(APP_ROLES.map(r => [r, 0]));
    users.forEach(u => {
      if (u.reportingOnly) return;
      const seen = new Set();
      if (Array.isArray(u.roles)) {
        u.roles.forEach(r => {
          if (APP_ROLES.includes(r)) seen.add(r);
        });
      }
      if (seen.size === 0 && u.role && APP_ROLES.includes(u.role)) {
        seen.add(u.role);
      }
      seen.forEach(r => {
        counts[r] += 1;
      });
    });
    return counts;
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (roleFilter === null) return users;
    return users.filter(
      u =>
        (Array.isArray(u.roles) && u.roles.includes(roleFilter)) || u.role === roleFilter
    );
  }, [users, roleFilter]);

  const salesLocationDropdownCounts = useMemo(() => {
    let unspecified = 0;
    const byId = Object.fromEntries(
      (salesLocationOptions || []).map(s => [String(s._id), 0])
    );
    filteredUsers.forEach(u => {
      if (u.reportingOnly) return;
      const ids = getUserSalesLocationIds(u);
      if (ids.length === 0) {
        unspecified += 1;
        return;
      }
      ids.forEach(id => {
        const key = String(id);
        if (Object.prototype.hasOwnProperty.call(byId, key)) {
          byId[key] += 1;
        }
      });
    });
    return { all: filteredUsers.filter(user => !user.reportingOnly).length, unspecified, byId };
  }, [filteredUsers, salesLocationOptions]);

  const locationFilteredUsers = useMemo(
    () =>
      filteredUsers.filter(u => userMatchesSalesLocationFilter(u, salesLocationFilterId)),
    [filteredUsers, salesLocationFilterId]
  );

  const usersForTable = useMemo(
    () =>
      locationFilteredUsers.map(u => {
        const parts = [];
        if (Array.isArray(u.salesLocations) && u.salesLocations.length) {
          u.salesLocations.forEach(sl => {
            if (sl?.location) parts.push(String(sl.location).trim());
          });
        } else if (u.salesLocation?.location) {
          parts.push(String(u.salesLocation.location).trim());
        }
        return {
          ...u,
          salesLocationName: parts.join(' ')
        };
      }),
    [locationFilteredUsers]
  );

  const [showMultiSalesLocationField, setShowMultiSalesLocationField] = useState(false);

  useEffect(() => {
    if (!userActionMeta) {
      setShowMultiSalesLocationField(false);
      return;
    }
    if (userActionMeta.mode === 'add') {
      setShowMultiSalesLocationField(false);
    } else if (
      userActionMeta.user &&
      isMultiLocationRole(userActionMeta.user.roles)
    ) {
      setShowMultiSalesLocationField(true);
    }

    const id = setInterval(() => {
      const el = document.getElementById('roles');
      if (!el) return;
      const raw = el.getAttribute('data-selections') || '';
      const parts = raw.split(',').filter(Boolean);
      setShowMultiSalesLocationField(isMultiLocationRole(parts));
    }, 200);
    return () => clearInterval(id);
  }, [userActionMeta]);

  const userAddRows = useMemo(() => {
    const rows = getUserAddRows(isSuperAdmin());
    return applyUserFormSalesLocationFieldVisibility(rows, showMultiSalesLocationField);
  }, [showMultiSalesLocationField]);

  const userUpdateRows = useMemo(() => {
    const rows = getUserUpdateRows(isSuperAdmin());
    return applyUserFormSalesLocationFieldVisibility(rows, showMultiSalesLocationField);
  }, [showMultiSalesLocationField]);

  const fetchUsers = async () => {
    setLoading(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_LIST,
      method: 'POST'
    });

    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setUsers(response);
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    setSalesRepsOptions(
      users.filter(
        user =>
          Array.isArray(user.roles) &&
          user.roles.some(r => ROLES_FOR_SALES_REP_MANAGER_FILTER.includes(r))
      )
    );
  }, [users]);

  const deleteUser = async () => {
    setDeleteInProgress(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_BASE,
      requestBody: {
        email: toBeDeletedUser.email
      },
      method: 'DELETE'
    });

    setDeleteInProgress(false);
    if (error) {
      toast.error(error);
      return;
    }

    users.splice(
      users.findIndex(i => i._id === toBeDeletedUser._id),
      1
    );
    setUsers([...users]);
    setToBeDeletedUser(null);
  };

  const onNewUserFormSubmit = async form => {
    const payload = { ...form };
    if (payload.enabled !== undefined) payload.enabled = payload.enabled === 'true';
    const roles = Array.isArray(payload.roles) ? payload.roles : [];
    delete payload.roles;
    delete payload.role;

    if (roles.length === 0) {
      return toast.error('Select at least one role.');
    }

    if (roles.includes(ROLE_INSTALLER) && !payload.installer) {
      return toast.error('Please select an installer for this user!');
    }

    if (isMultiLocationRole(roles)) {
      payload.salesLocations = Array.isArray(payload.salesLocations) ? payload.salesLocations : [];
      delete payload.salesLocation;
      if (roles.includes(ROLE_SALES) && !roles.includes(ROLE_ADMIN) && payload.salesLocations.length === 0) {
        return toast.error('Sales location is required for sales members!');
      }
    } else {
      delete payload.salesLocations;
      if (roles.some(r => ROLES_NEEDING_SALES_LOCATION.includes(r)) && !payload.salesLocation) {
        return toast.error('Sales location is required for sales members!');
      }
    }

    setUserAddingUpdating(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_BASE,
      requestBody: { ...payload, roles }
    });

    setUserAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    setUsers([response, ...users]);
    setUserActionMeta(null);
  };

  const onCopyToClipboardClick = userList => {
    copyToClipboard(userList.filter(user => !user.reportingOnly).map(user => user.email).join(' ,'));
    showCopyFeedback('main');
  };

  const onUpdateUserFormSubmit = async form => {
    const isSalesRepEdit = userActionMeta?.mode === 'salesrep-edit';
    const payload = isSalesRepEdit
      ? { salesReps: Array.isArray(form.salesReps) ? form.salesReps : [] }
      : { ...form };
    let roles;

    if (!isSalesRepEdit) {
      roles = Array.isArray(payload.roles) ? payload.roles : [];
    if (payload.enabled !== undefined) payload.enabled = payload.enabled === 'true';
    delete payload.roles;
    delete payload.role;

    if (roles.length === 0) {
      return toast.error('Select at least one role.');
    }

    if (roles.includes(ROLE_INSTALLER) && !payload.installer) {
      return toast.error('Please select an installer for this user!');
    }

    if (isMultiLocationRole(roles)) {
      payload.salesLocations = Array.isArray(payload.salesLocations) ? payload.salesLocations : [];
      delete payload.salesLocation;
      if (roles.includes(ROLE_SALES) && !roles.includes(ROLE_ADMIN) && payload.salesLocations.length === 0) {
        return toast.error('Sales location is required for sales members!');
      }
    } else {
      payload.salesLocations = [];
      if (roles.some(r => ROLES_NEEDING_SALES_LOCATION.includes(r)) && !payload.salesLocation) {
        return toast.error('Sales location is required for sales members!');
      }
      }
    }

    setUserAddingUpdating(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.USERS_UPDATE,
      requestBody: { ...payload, ...(roles && { roles }), email: userActionMeta.user.email }
    });

    setUserAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    const userIndex = users.findIndex(i => i._id === response._id);
    users[userIndex] = { ...users[userIndex], ...response };
    setUsers([...users]);
    setUserActionMeta(null);
    toast.success(
      isSalesRepEdit ? 'Sales representatives updated successfully!' : 'User updated successfully!'
    );
  };

  window['onNewUserFormSubmit'] = onNewUserFormSubmit;
  window['onUpdateUserFormSubmit'] = onUpdateUserFormSubmit;

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <h6>Users</h6>
            {!loading && !userAddingUpdating && (
              <span
                style={copyBtnWrapStyle}
                title={
                  roleFilter === null && !salesLocationFilterId
                    ? 'Copy all emails'
                    : 'Copy visible users’ emails'
                }
              >
                {copiedTarget === 'main' ? (
                  <Check2 size={20} style={copyCheckDoneStyle} />
                ) : (
                  <Clipboard
                    className="pointer"
                    size={20}
                    onClick={() => onCopyToClipboardClick(locationFilteredUsers)}
                  />
                )}
              </span>
            )}
          </div>
          <hr className="my-3" />
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: '0.5rem' }}>
                <Badge
                  pill
                  variant={roleFilter === null ? 'primary' : 'light'}
                  className={`py-2 px-3 ${roleFilter === null ? '' : 'text-dark border'}`}
                  role="button"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setRoleFilter(null)}
                >
                  All ({users.filter(user => !user.reportingOnly).length})
                </Badge>
                {APP_ROLES.map(role => (
                  <Badge
                    key={role}
                    pill
                    variant={roleFilter === role ? 'primary' : 'light'}
                    className={`py-2 px-3 ${roleFilter === role ? '' : 'text-dark border'}`}
                    role="button"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setRoleFilter(roleFilter === role ? null : role)}
                  >
                    {role} ({roleCounts[role]})
                  </Badge>
                ))}
              </div>
              <EntityTable
                rowKeyField={'_id'}
                data={usersForTable}
                hideUntilSearchKey="reportingOnly"
                salesLocation
                headers={userTableHeaders}
                searchQueryKeys={userTableSearchKeys}
                additionalComponents={() => (
                  <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '0.5rem' }}>
                    <Form.Control
                      as="select"
                      size="sm"
                      className="users-location-filter"
                      style={{ minWidth: 200, maxWidth: 280, width: 'auto', flex: '0 0 auto' }}
                      value={salesLocationFilterId}
                      onChange={e => setSalesLocationFilterId(e.target.value)}
                      aria-label="Filter by sales location"
                    >
                      <option value="">
                        All Locations ({salesLocationDropdownCounts.all})
                      </option>
                      <option value={SALES_LOCATION_FILTER_UNSPECIFIED}>
                        Unspecified Location ({salesLocationDropdownCounts.unspecified})
                      </option>
                      {(salesLocationOptions || []).map(s => (
                        <option key={s._id} value={s._id}>
                          {s.location} ({salesLocationDropdownCounts.byId[String(s._id)] ?? 0})
                        </option>
                      ))}
                    </Form.Control>
                    <Button
                      size="sm"
                      variant="success"
                      className="flex-shrink-0 text-nowrap"
                      onClick={() => setUserActionMeta({ mode: 'add' })}
                    >
                      <PlusCircle className="mr-1" /> New user
                    </Button>
                  </div>
                )}
                actionCell={user => (
                  <div>
                    {Array.isArray(user.roles) && user.roles.some(r => r === ROLE_SALES_MANAGER) && (
                      <Button
                        variant="outline-primary"
                        className="px-1 py-0 mr-1"
                        onClick={() => setUserActionMeta({ user, mode: 'salesrep-edit' })}
                      >
                        <PeopleFill size={10} />
                      </Button>
                    )}
                    {!user.reportingOnly && (isSuperAdmin() ||
                      !(Array.isArray(user.roles) && user.roles.some(r => ADMIN_ROLES.includes(r)))) && (
                        <>
                          <Button
                            variant="outline-primary"
                            className="px-1 py-0 mr-1"
                            onClick={() => setUserActionMeta({ user, mode: 'edit' })}
                          >
                            <Pen size={10} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            className="px-1 py-0 "
                            onClick={() => setToBeDeletedUser(user)}
                          >
                            <Trash size={10} />
                          </Button>
                        </>
                      )}
                  </div>
                )}
              />
              {users.length > 0 && (
                <AlertModal
                  show={toBeDeletedUser !== null}
                  alertText={`Are you sure you want to delete this user? This action cannot be undone!`}
                  onHide={() => setToBeDeletedUser(null)}
                  onDismissClick={() => setToBeDeletedUser(null)}
                  onContinueClick={deleteUser}
                  progressText={`Deleting user...`}
                  showProgress={deleteInProgress}
                />
              )}
              <AddEditModal
                show={userActionMeta !== null}
                onHide={() => setUserActionMeta(null)}
                toBeEditedUser={userActionMeta ? userActionMeta.user : null}
                showProgress={userAddingUpdating}
                isForSalesReps={userActionMeta && userActionMeta.mode === 'salesrep-edit'}
                addForm={getForm({ rows: userAddRows, salesLocationOptions, installerOptions })}
                updateForm={getForm({
                  rows:
                    userActionMeta && userActionMeta.mode === 'salesrep-edit'
                      ? userSalesRepRow(salesRepsOptions, userActionMeta?.user)
                      : userUpdateRows,
                  type: 'Update',
                  salesLocationOptions,
                  installerOptions
                })}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Users;
